import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, nextTick } from "vue";
import { timepickerProps, timepickerEmits, type TimeUnit, type TimeValue, type TimeObject } from "./timepicker";


export default /*#__PURE__*/_defineComponent({
  __name: 'timepicker',
  props: timepickerProps,
  emits: timepickerEmits,
  setup(__props, { emit: emits }) {

const props = __props




const ITEM_HEIGHT = 40;

const showHours = computed(() => ["HH:mm:ss", "HH:mm", "HH"].includes(props.format));
const showMinutes = computed(() => ["HH:mm:ss", "HH:mm", "mm"].includes(props.format));
const showSeconds = computed(() => ["HH:mm:ss", "ss"].includes(props.format));

const timeOptions = computed<{ hours: number[]; minutes: number[]; seconds: number[] }>(() => {
    const options = {
        hours: Array.from({ length: 24 }, (_, i) => i),
        minutes: Array.from({ length: 60 }, (_, i) => i),
        seconds: Array.from({ length: 60 }, (_, i) => i),
    };

    if (props.only === "hours") {
        options.minutes = [0];
        options.seconds = [0];
    } else if (props.only === "minutes") {
        options.hours = [0];
        options.seconds = [0];
    } else if (props.only === "seconds") {
        options.hours = [0];
        options.minutes = [0];
    }

    return options;
});

const selectedHour = ref(0);
const selectedMinute = ref(0);
const selectedSecond = ref(0);

const hourColumn = ref<HTMLElement | null>(null);
const minuteColumn = ref<HTMLElement | null>(null);
const secondColumn = ref<HTMLElement | null>(null);

const isDragging = ref(false);
const startY = ref(0);
const startScrollTop = ref(0);
const activeColumn = ref<TimeUnit | null>(null);
const scrollTimeout = ref<number | null>(null);
const isPotentialClick = ref(true);

const formatNumber = (num: number): string => num < 10 ? `0${num}` : num.toString();

const parseTime = (time?: TimeValue): { hours: number; minutes: number; seconds: number } => {
    if (!time) {
        const now = new Date();
        return { hours: now.getHours(), minutes: now.getMinutes(), seconds: now.getSeconds() };
    }

    if (typeof time === "string") {
        const parts = time.split(":").map(part => parseInt(part) || 0);

        switch (props.format) {
        case "HH:mm":
            return { hours: parts[0], minutes: parts[1], seconds: 0 };
        case "HH":
            return { hours: parts[0], minutes: 0, seconds: 0 };
        case "mm":
            return { hours: 0, minutes: parts[0], seconds: 0 };
        case "ss":
            return { hours: 0, minutes: 0, seconds: parts[0] };
        default:
            return { hours: parts[0], minutes: parts[1], seconds: parts[2] };
        }
    }

    if (time instanceof Date) {
        return { hours: time.getHours(), minutes: time.getMinutes(), seconds: time.getSeconds() };
    }

    if (typeof time === "object") {
        return {
            hours: parseInt(String(time.hours ?? 0)) || 0,
            minutes: parseInt(String(time.minutes ?? 0)) || 0,
            seconds: parseInt(String(time.seconds ?? 0)) || 0,
        };
    }

    return { hours: 0, minutes: 0, seconds: 0 };
};

const getColumnElement = (column: TimeUnit): HTMLElement | null =>
    column === "hours" ? hourColumn.value : column === "minutes" ? minuteColumn.value : secondColumn.value;

const getSelectedValue = (column: TimeUnit): number =>
    column === "hours" ? selectedHour.value : column === "minutes" ? selectedMinute.value : selectedSecond.value;

const clampValue = (column: TimeUnit, value: number): number => {
    if (column === "hours") return Math.max(0, Math.min(23, value));
    return Math.max(0, Math.min(59, value));
};

const setSelectedValue = (column: TimeUnit, value: number): void => {
    const clamped = clampValue(column, value);
    if (column === "hours") selectedHour.value = clamped;
    else if (column === "minutes") selectedMinute.value = clamped;
    else selectedSecond.value = clamped;
};

const emitValue = (): void => {
    if (props.returnObject) {
        const time: TimeObject = {};
        if (showHours.value) time.hours = formatNumber(selectedHour.value);
        if (showMinutes.value) time.minutes = formatNumber(selectedMinute.value);
        if (showSeconds.value) time.seconds = formatNumber(selectedSecond.value);
        emits("update:modelValue", time);
    } else {
        let timeStr = "";
        if (showHours.value) timeStr += formatNumber(selectedHour.value);
        if (showMinutes.value) timeStr += (timeStr ? ":" : "") + formatNumber(selectedMinute.value);
        if (showSeconds.value) timeStr += (timeStr ? ":" : "") + formatNumber(selectedSecond.value);
        emits("update:modelValue", timeStr);
    }
};

const scrollToSelected = (column: TimeUnit, behavior: ScrollBehavior = "auto"): void => {
    const element = getColumnElement(column);
    if (!element) return;

    element.scrollTo({ top: getSelectedValue(column) * ITEM_HEIGHT, behavior });
};

const handleScroll = (column: TimeUnit): void => {
    if (isDragging.value) return;

    const element = getColumnElement(column);
    if (!element) return;

    if (scrollTimeout.value) clearTimeout(scrollTimeout.value);

    scrollTimeout.value = setTimeout(() => {
        const value = Math.round(element.scrollTop / ITEM_HEIGHT);
        setSelectedValue(column, value);
        emitValue();
        scrollToSelected(column);
    }, 100) as unknown as number;
};

const startDrag = (e: MouseEvent | TouchEvent, column: TimeUnit): void => {
    isPotentialClick.value = true;
    isDragging.value = false;
    activeColumn.value = column;
    startY.value = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;

    const element = getColumnElement(column);
    if (element) {
        startScrollTop.value = element.scrollTop;
        element.style.scrollSnapType = "none";
    }

    if (e instanceof MouseEvent) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", stopDrag);
    }
};

const handleMouseMove = (e: MouseEvent): void => {
    if (Math.abs(e.clientY - startY.value) > 5) {
        isDragging.value = true;
        isPotentialClick.value = false;
        onDrag(e);
    }
};

const onDrag = (e: MouseEvent | TouchEvent): void => {
    if (!isDragging.value || !activeColumn.value) return;

    const y = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;
    const element = getColumnElement(activeColumn.value);

    if (element) {
        element.scrollTop = startScrollTop.value - (y - startY.value);
        const value = Math.round(element.scrollTop / ITEM_HEIGHT);
        setSelectedValue(activeColumn.value, value);
        emitValue();
    }
};

const stopDrag = (): void => {
    if (!activeColumn.value) return;

    const element = getColumnElement(activeColumn.value);
    if (element) {
        const value = Math.round(element.scrollTop / ITEM_HEIGHT);
        setSelectedValue(activeColumn.value, value);
        scrollToSelected(activeColumn.value, "smooth");
        emitValue();
    }

    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopDrag);

    setTimeout(() => {
        isDragging.value = false;
        isPotentialClick.value = true;
    }, 50);
};

const adjustTime = (column: TimeUnit, value: number): void => {
    setSelectedValue(column, value);
    scrollToSelected(column, "smooth");
    emitValue();
};

const handleOptionClick = (event: MouseEvent, column: TimeUnit, value: number): void => {
    if (isDragging.value || !isPotentialClick.value) {
        event.preventDefault();
        event.stopPropagation();
        return;
    }
    adjustTime(column, value);
};

const handleOptionMouseDown = (): void => {
    isPotentialClick.value = true;
};

const initialize = (): void => {
    const { hours, minutes, seconds } = parseTime(props.modelValue);
    selectedHour.value = hours;
    selectedMinute.value = minutes;
    selectedSecond.value = seconds;

    nextTick(() => {
        if (showHours.value) scrollToSelected("hours");
        if (showMinutes.value) scrollToSelected("minutes");
        if (showSeconds.value) scrollToSelected("seconds");
    });
};

watch(() => props.modelValue, initialize, { immediate: true });

return { __sfc: true,props, emits, ITEM_HEIGHT, showHours, showMinutes, showSeconds, timeOptions, selectedHour, selectedMinute, selectedSecond, hourColumn, minuteColumn, secondColumn, isDragging, startY, startScrollTop, activeColumn, scrollTimeout, isPotentialClick, formatNumber, parseTime, getColumnElement, getSelectedValue, clampValue, setSelectedValue, emitValue, scrollToSelected, handleScroll, startDrag, handleMouseMove, onDrag, stopDrag, adjustTime, handleOptionClick, handleOptionMouseDown, initialize }
}

})